import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GlassIconButton } from 'dashboard/components/common';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';

const variantLabel = (v) => {
  const attrs = v?.attributes
    ? (v.attributes instanceof Map
        ? Array.from(v.attributes.entries())
        : Object.entries(v.attributes || {}))
    : [];
  const tail = attrs.map(([k, val]) => `${k}:${val}`).join(' · ');
  return `${v.sku || v._id}${tail ? ` — ${tail}` : ''}`;
};

/**
 * Move stock between two warehouses. Blocks an overdraft client-side
 * so the user can't fire a transfer the server will reject anyway.
 */
export default function TransferStockDialog({
  open, onClose, brandId, warehouses, variants, levelsByKey, onTransferred,
}) {
  const [variantId, setVariantId] = useState('');
  const [fromWh, setFromWh] = useState('');
  const [toWh, setToWh] = useState('');
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setVariantId(variants[0]?._id || '');
      setFromWh(warehouses[0]?._id || '');
      setToWh(warehouses[1]?._id || '');
      setQty(1); setNote('');
    }
  }, [open, variants, warehouses]);

  const sourceLevel = variantId && fromWh ? levelsByKey[`${variantId}:${fromWh}`] : null;
  const available = sourceLevel
    ? Math.max(0, (sourceLevel.on_hand || 0) - (sourceLevel.reserved || 0))
    : 0;
  const overdraft = Number(qty) > available;

  const transfer = async () => {
    if (!variantId || !fromWh || !toWh) { toast.error('Pick a variant and both warehouses'); return; }
    if (fromWh === toWh) { toast.error('Source and destination must differ'); return; }
    setBusy(true);
    try {
      await API.transferShopStock({
        brand_id: brandId,
        variant_id: variantId,
        from_warehouse_id: fromWh,
        to_warehouse_id: toWh,
        quantity: Number(qty),
        note,
      });
      toast.success(`Moved ${qty} unit${qty === 1 ? '' : 's'}`);
      onTransferred && onTransferred();
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Transfer failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth scroll='paper'>
      <DialogTitle sx={{ pr: '48px' }}>
        Transfer stock
        <GlassIconButton label='Close'
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          size='small'
        >
          <CloseIcon fontSize='small' />
        </GlassIconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing='12px'>
          <FormControl size='small'>
            <InputLabel>Variant</InputLabel>
            <Select
              label='Variant'
              value={variantId}
              onChange={(e) => setVariantId(e.target.value)}
            >
              {variants.map((v) => (
                <MenuItem key={v._id} value={v._id}>{variantLabel(v)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing='10px'>
            <FormControl size='small' fullWidth>
              <InputLabel>From warehouse</InputLabel>
              <Select
                label='From warehouse'
                value={fromWh}
                onChange={(e) => setFromWh(e.target.value)}
              >
                {warehouses.map((w) => (
                  <MenuItem key={w._id} value={w._id}>{w.code || w.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size='small' fullWidth>
              <InputLabel>To warehouse</InputLabel>
              <Select
                label='To warehouse'
                value={toWh}
                onChange={(e) => setToWh(e.target.value)}
              >
                {warehouses.map((w) => (
                  <MenuItem key={w._id} value={w._id}>{w.code || w.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <TextField
            size='small'
            type='number'
            label='Quantity'
            inputProps={{ min: '1' }}
            value={qty}
            onChange={(e) => setQty(Math.max(1, Math.floor(Number(e.target.value || 1))))}
            helperText={
              <Typography
                variant='caption'
                component='span'
                color={overdraft ? 'error.main' : 'text.secondary'}
              >
                Available at source: {available}{overdraft && ' — moving more than this is not allowed'}
              </Typography>
            }
          />
          <TextField
            size='small'
            label='Note (optional)'
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder='e.g. restocking pop-up store'
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: '14px 20px' }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant='contained'
          onClick={transfer}
          disabled={busy || overdraft || !variantId || !fromWh || !toWh || fromWh === toWh}
        >
          {busy ? 'Moving…' : 'Transfer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
